import { memo, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { DEFAULT_VISIBILITY_MAP } from '../../constants/visibility';
import { useFunnelMapElements } from '../funnel-map-canvas/useFunnelMapElements';
import { selectCrmOrgs } from '../pages/environments/environmentsReducer';
import { normalizeForPreviewData } from './normalizeForPreviewData';
import { renderPreviewToBlob } from './renderPreviewToCanvas';
import { usePreviewCacheOptional } from './PreviewCacheContext';
import { debugLog } from './previewDebugLogger';

interface BatchTimingRef {
  startTime: number;
  totalCount: number;
  completedCount: number;
  totalRenderTime: number;
}

interface PreviewGeneratorProps {
  funnelMap: FunnelMap;
  onComplete: (funnelMapId: string, blob: Blob | null, renderTimeMs: number) => void;
}

const PreviewGenerator = memo(({ funnelMap, onComplete }: PreviewGeneratorProps) => {
  const crmOrgs = useSelector(selectCrmOrgs);

  const { sweepNodes, sweepEdges, sweepGroups, sweepStandaloneNodes, sweepSections } =
    useFunnelMapElements({
      funnelMap,
      visibilityMap: DEFAULT_VISIBILITY_MAP,
      objectTypesByName: {},
      crmOrgs: crmOrgs,
    });

  useEffect(() => {
    if (sweepNodes.length + sweepStandaloneNodes.length === 0) {
      debugLog('[PreviewPrecompute]', `Skipping "${funnelMap.name}" - no nodes to render`);
      onComplete(funnelMap.id, null, 0);
      return;
    }

    const generatePreview = async () => {
      debugLog(
        '[PreviewPrecompute]',
        `Generating preview for "${funnelMap.name}" (${sweepNodes.length} nodes, ${sweepStandaloneNodes.length} standalone)`,
      );
      const startTime = performance.now();

      const previewData = normalizeForPreviewData({
        sweepNodes,
        sweepGroups,
        sweepEdges,
        sweepSections,
        sweepStandaloneNodes,
      });

      const blob = await renderPreviewToBlob(previewData, { backgroundColor: '#FFFFFF' });

      const renderTimeMs = performance.now() - startTime;
      debugLog(
        '[PreviewPrecompute]',
        `Preview generated for "${funnelMap.name}" in ${renderTimeMs.toFixed(0)}ms | size=${blob ? (blob.size / 1024).toFixed(1) + 'KB' : 'null'}`,
      );

      onComplete(funnelMap.id, blob, renderTimeMs);
    };

    generatePreview();
  }, [
    funnelMap.id,
    funnelMap.name,
    sweepNodes,
    sweepEdges,
    sweepGroups,
    sweepStandaloneNodes,
    sweepSections,
    onComplete,
  ]);

  return null;
});

PreviewGenerator.displayName = 'PreviewGenerator';

interface PreviewPrecomputeProps {
  funnelMaps: FunnelMap[];
}

export const PreviewPrecompute = memo(({ funnelMaps }: PreviewPrecomputeProps) => {
  const cache = usePreviewCacheOptional();
  const batchTimingRef = useRef<BatchTimingRef | null>(null);
  const lastBatchIdRef = useRef<string | null>(null);

  const funnelMapsToPrecompute = useMemo(() => {
    if (!cache || !cache.isHydrated) {
      debugLog('[PreviewPrecompute]', 'Waiting for cache hydration...');
      return [];
    }

    const missing = funnelMaps.filter((fm) => {
      return !cache.hasPreview(fm);
    });

    debugLog(
      '[PreviewPrecompute]',
      `Precompute check: ${missing.length}/${funnelMaps.length} funnel maps need preview generation`,
    );
    if (missing.length > 0) {
      debugLog(
        '[PreviewPrecompute]',
        `Missing previews for: ${missing.map((fm) => fm.name).join(', ')}`,
      );
    }

    return missing;
  }, [funnelMaps, cache]);

  // Generate a batch ID based on the funnel map IDs to precompute
  const currentBatchId = useMemo(() => {
    if (funnelMapsToPrecompute.length === 0) return null;
    return funnelMapsToPrecompute.map((fm) => fm.id).join(',');
  }, [funnelMapsToPrecompute]);

  // Initialize batch timing using useLayoutEffect - runs synchronously before children's useEffects
  useLayoutEffect(() => {
    if (currentBatchId !== null && currentBatchId !== lastBatchIdRef.current) {
      lastBatchIdRef.current = currentBatchId;
      batchTimingRef.current = {
        startTime: performance.now(),
        totalCount: funnelMapsToPrecompute.length,
        completedCount: 0,
        totalRenderTime: 0,
      };
      debugLog(
        '[PreviewPrecompute]',
        `⏱️ Starting batch render of ${funnelMapsToPrecompute.length} previews`,
      );
    } else if (currentBatchId === null && lastBatchIdRef.current !== null) {
      lastBatchIdRef.current = null;
    }
  }, [currentBatchId, funnelMapsToPrecompute.length]);

  const handleComplete = useMemo(() => {
    if (!cache) return () => {};

    return (funnelMapId: string, blob: Blob | null, renderTimeMs: number) => {
      const funnelMap = funnelMaps.find((fm) => fm.id === funnelMapId);
      if (funnelMap && blob) {
        cache.setPreview(funnelMap, blob);
      }

      // Update batch timing
      if (batchTimingRef.current) {
        batchTimingRef.current.completedCount++;
        batchTimingRef.current.totalRenderTime += renderTimeMs;

        const { completedCount, totalCount, startTime, totalRenderTime } = batchTimingRef.current;

        if (completedCount === totalCount) {
          const wallClockTime = (performance.now() - startTime).toFixed(0);
          const avgRenderTime = (totalRenderTime / totalCount).toFixed(0);
          debugLog(
            '[PreviewPrecompute]',
            `⏱️ Batch complete: ${totalCount} previews | wall-clock=${wallClockTime}ms | total-render=${totalRenderTime.toFixed(0)}ms | avg=${avgRenderTime}ms/preview`,
          );
          batchTimingRef.current = null;
        }
      }
    };
  }, [cache, funnelMaps]);

  if (!cache) {
    return null;
  }

  return (
    <>
      {funnelMapsToPrecompute.map((funnelMap) => (
        <PreviewGenerator
          key={`${funnelMap.id}-${funnelMap.version}`}
          funnelMap={funnelMap}
          onComplete={handleComplete}
        />
      ))}
    </>
  );
});

PreviewPrecompute.displayName = 'PreviewPrecompute';
